import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
    useReactTable,
    getCoreRowModel,
    getFilteredRowModel,
    getSortedRowModel,
    getPaginationRowModel,
    flexRender,
    type ColumnDef,
    type SortingState,
} from '@tanstack/react-table';
import { Button } from '@/kit';
import { platformApi } from '../../../../api/endpoints/platform';
import { ContentLoading } from '../../../../components/shared/ContentLoading';
import { Gate } from '../../../../components/permissions/Gate';
import { Badge } from '../../../../components/ui/badge';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../../../../components/ui/table';
import { DataTableToolbar } from '../../../../components/shared/DataTableToolbar';
import { DataTablePagination } from '../../../../components/shared/DataTablePagination';
import { SortableHeader } from '../../../../components/shared/SortableHeader';
import { HelpHint } from '@/components/ui/HelpHint';
import { HELP_TEXT } from '@/lib/helpText';
import { globalFilterFn } from '../../../../lib/dataTable';
import { PlatformTenantRoleForm } from './PlatformTenantRoleForm';
import { PlatformTenantRoleRowActions } from './PlatformTenantRoleRowActions';
import type { TenantRoleListItemLocal } from '../../../../api/types/TenantLocal';

// Los 4 roles seed (predefinidos) del tenant. Mismo criterio que la RolesTable del flujo tenant: el
// seed del backend OMITE el flag isSystem (limitación del GSI), así que sin este fallback por slug
// todos caerían a "Custom" y serían borrables. Estos 4 son system por invariante (no editables/borrables).
const SYSTEM_ROLE_IDS = new Set([
    'tenant_admin',
    'retailer_admin',
    'store_manager',
    'collection_agent',
]);

function isSystemRole(role: TenantRoleListItemLocal): boolean {
    return role.isSystem || SYSTEM_ROLE_IDS.has(role.roleId);
}

interface Props {
    /** tenantId del DRILL-DOWN (param de ruta). Toda la data se consume contra ESTE tenant. */
    tenantId: string;
}

/**
 * Roles de un tenant en el módulo Platform (drill-down). Misma composición que la RolesTable del
 * flujo tenant (useReactTable + DataTableToolbar/Pagination + SortableHeader + kebab por fila), pero
 * el `tenantId` viene por PROP (del path) y la data sale de `platformApi.*TenantRole`. Crear rol
 * gateado por `tenant.role.create` (con scope TENANT/tenantId; el platform admin lo cubre por rank).
 * Solo los roles custom traen menú de acciones (editar/eliminar); los de sistema son inmutables.
 */
export function PlatformTenantRolesTable({ tenantId }: Props) {
    const [createOpen, setCreateOpen] = useState(false);
    const [globalFilter, setGlobalFilter] = useState('');
    const [sorting, setSorting] = useState<SortingState>([{ id: 'roleName', desc: false }]);

    const { data, isLoading } = useQuery({
        queryKey: ['platform', 'tenants', tenantId, 'roles'],
        queryFn: () => platformApi.listTenantRoles(tenantId),
        enabled: !!tenantId,
    });

    // Columnas estables (footgun de useReactTable). El slug (roleId) va como columna de texto buscable.
    const columns = useMemo<ColumnDef<TenantRoleListItemLocal>[]>(
        () => [
            {
                accessorKey: 'roleName',
                header: ({ column }) => (
                    <span className="inline-flex items-center gap-1">
                        <SortableHeader column={column} title="Rol" />
                        <HelpHint text={HELP_TEXT.role} side="right" />
                    </span>
                ),
            },
            {
                id: 'roleId',
                accessorFn: (row) => row.roleId,
                header: ({ column }) => <SortableHeader column={column} title="Slug" />,
                cell: (info) => (
                    <span className="text-muted-foreground">{info.getValue() as string}</span>
                ),
            },
            {
                id: 'scope',
                accessorFn: (row) => row.scope ?? 'TENANT',
                header: ({ column }) => (
                    <span className="inline-flex items-center gap-1">
                        <SortableHeader column={column} title="Alcance" />
                        <HelpHint text={HELP_TEXT.scope} side="right" />
                    </span>
                ),
                cell: (info) => <Badge variant="outline">{info.getValue() as string}</Badge>,
            },
            {
                id: 'tipo',
                accessorFn: (row) => (isSystemRole(row) ? 'Sistema' : 'Custom'),
                header: ({ column }) => <SortableHeader column={column} title="Tipo" />,
                cell: (info) => {
                    const system = isSystemRole(info.row.original);
                    return (
                        <Badge variant={system ? 'secondary' : 'default'}>
                            {system ? 'Sistema' : 'Custom'}
                        </Badge>
                    );
                },
            },
            {
                id: 'permissions',
                accessorFn: (row) => row.permissions?.length ?? 0,
                header: ({ column }) => (
                    <span className="inline-flex items-center gap-1">
                        <SortableHeader column={column} title="Permisos" />
                        <HelpHint text={HELP_TEXT.permission} side="right" />
                    </span>
                ),
                enableGlobalFilter: false,
            },
            {
                id: 'actions',
                header: 'Acciones',
                enableSorting: false,
                enableGlobalFilter: false,
                // Todos los roles (incluidos los de Sistema) tienen "Ver detalle" (read-only). Editar/
                // eliminar solo los custom — eso lo decide RowActions con `isSystem`.
                cell: (info) => (
                    <PlatformTenantRoleRowActions
                        role={info.row.original}
                        tenantId={tenantId}
                        isSystem={isSystemRole(info.row.original)}
                    />
                ),
            },
        ],
        [tenantId],
    );

    const rows = useMemo(() => data?.items ?? [], [data]);
    const table = useReactTable({
        data: rows,
        columns,
        state: { globalFilter, sorting },
        onGlobalFilterChange: setGlobalFilter,
        onSortingChange: setSorting,
        globalFilterFn,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        initialState: { pagination: { pageSize: 25 } },
    });

    if (isLoading) return <ContentLoading />;

    return (
        <div className="space-y-3">
            <div className="flex justify-end">
                <Gate permission="tenant.role.create" scope="TENANT" scopeRef={tenantId}>
                    <Button
                        onClick={() => {
                            setCreateOpen(true);
                        }}
                    >
                        Crear rol custom
                    </Button>
                </Gate>
            </div>

            {/* Modal plano (mismo patrón que UsersTable): crear rol custom sin navegar de página. */}
            {createOpen && (
                <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-4 pt-16">
                    <button
                        type="button"
                        aria-label="Cerrar"
                        className="fixed inset-0 bg-black/50"
                        onClick={() => {
                            setCreateOpen(false);
                        }}
                    />
                    <div className="relative w-full max-w-lg rounded-lg border bg-background p-6 shadow-lg">
                        <div className="mb-4 flex items-center justify-between">
                            <h2 className="text-lg font-semibold">Crear rol custom</h2>
                            <button
                                type="button"
                                aria-label="Cerrar"
                                className="text-muted-foreground hover:text-foreground"
                                onClick={() => {
                                    setCreateOpen(false);
                                }}
                            >
                                ✕
                            </button>
                        </div>
                        <PlatformTenantRoleForm
                            tenantId={tenantId}
                            onDone={() => {
                                setCreateOpen(false);
                            }}
                        />
                    </div>
                </div>
            )}

            <DataTableToolbar
                value={globalFilter}
                onChange={setGlobalFilter}
                placeholder="Buscar por nombre o slug…"
            />

            <Table>
                <TableHeader>
                    {table.getHeaderGroups().map((hg) => (
                        <TableRow key={hg.id}>
                            {hg.headers.map((h) => (
                                <TableHead key={h.id} className="text-center">
                                    {flexRender(h.column.columnDef.header, h.getContext())}
                                </TableHead>
                            ))}
                        </TableRow>
                    ))}
                </TableHeader>
                <TableBody>
                    {table.getRowModel().rows.map((r) => (
                        <TableRow key={r.id}>
                            {r.getVisibleCells().map((c) => (
                                <TableCell key={c.id} className="text-center align-middle">
                                    {flexRender(c.column.columnDef.cell, c.getContext())}
                                </TableCell>
                            ))}
                        </TableRow>
                    ))}
                </TableBody>
            </Table>

            <DataTablePagination table={table} />
        </div>
    );
}
